/**
 * Mining Industry Configuration
 *
 * Supports: Haul Trucks, Excavators, Loaders, Crushers, Conveyor Systems, Drill Rigs
 */

import { IndustryConfig } from '../industryTypes'

export const miningConfig: IndustryConfig = {
  id: 'mining',
  name: 'Mining',
  description: 'Surface and underground mining equipment monitoring and maintenance',
  icon: 'Mountain',

  terminology: {
    asset: 'Equipment',
    assets: 'Equipment',
    assetPlural: 'Equipment Fleet',
    assetType: 'Heavy Equipment Type',
    failure: 'Breakdown',
    health: 'Condition',
    healthScore: 'Equipment Health Score',
    healthy: 'Operational',
    degraded: 'Warning',
    critical: 'Down',
    alert: 'Alert',
    process: 'Extraction',
    maintenance: 'Maintenance',
    inspection: 'Inspection',
    downtime: 'Downtime',
    workOrder: 'Work Order',
    prediction: 'Forecast',
    anomaly: 'Anomaly',
    trend: 'Trend',
    risk: 'Risk',
    facility: 'Site',
    facilityPlural: 'Sites',
    location: 'Area',
    operator: 'Operator',
    supervisor: 'Supervisor',
    calibration: 'Calibration',
    compliance: 'Compliance',
    criticality: 'Criticality',
  },

  assetTypes: [
    {
      id: 'haul_truck',
      name: 'Haul Truck',
      icon: 'Truck',
      description: 'Off-highway haul trucks for ore and overburden transport',
      category: 'Haulage',
      defaultMetrics: ['payload', 'fuel_consumption', 'tire_pressure', 'engine_temperature', 'transmission_temp'],
      maintenanceTypes: ['tire_rotation', 'oil_change', 'brake_inspection', 'transmission_service'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary production hauler', downtimeCostMultiplier: 15 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'High-capacity unit', downtimeCostMultiplier: 10 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Standard production', downtimeCostMultiplier: 5 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Backup unit', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'excavator',
      name: 'Excavator',
      icon: 'Construction',
      description: 'Hydraulic excavators for loading and material handling',
      category: 'Loading',
      defaultMetrics: ['swing_torque', 'hydraulic_pressure', 'bucket_cycles', 'fuel_consumption', 'track_tension'],
      maintenanceTypes: ['hydraulic_service', 'track_adjustment', 'bucket_inspection', 'boom_inspection'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary loader at face', downtimeCostMultiplier: 20 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Main production excavator', downtimeCostMultiplier: 12 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Secondary loading', downtimeCostMultiplier: 6 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Auxiliary operations', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'loader',
      name: 'Loader',
      icon: 'Warehouse',
      description: 'Wheel loaders and front-end loaders for material handling',
      category: 'Loading',
      defaultMetrics: ['bucket_load', 'hydraulic_pressure', 'tire_wear', 'fuel_efficiency', 'cycle_time'],
      maintenanceTypes: ['tire_service', 'hydraulic_inspection', 'bucket_edge_replacement', 'transmission_service'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary stockpile loader', downtimeCostMultiplier: 12 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Production loader', downtimeCostMultiplier: 8 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Support operations', downtimeCostMultiplier: 4 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Utility loader', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'crusher',
      name: 'Crusher',
      icon: 'Hammer',
      description: 'Primary, secondary, and tertiary crushing equipment',
      category: 'Processing',
      defaultMetrics: ['throughput', 'power_draw', 'liner_wear', 'discharge_size', 'vibration'],
      maintenanceTypes: ['liner_replacement', 'jaw_adjustment', 'bearing_service', 'motor_inspection'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary crusher', downtimeCostMultiplier: 25 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Secondary crusher', downtimeCostMultiplier: 15 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Tertiary crusher', downtimeCostMultiplier: 8 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Standby crusher', downtimeCostMultiplier: 3 },
      ],
    },
    {
      id: 'conveyor_system',
      name: 'Conveyor System',
      icon: 'ArrowRightLeft',
      description: 'Belt conveyors for material transport between processing stages',
      category: 'Material Handling',
      defaultMetrics: ['belt_speed', 'belt_tension', 'motor_current', 'idler_condition', 'alignment'],
      maintenanceTypes: ['belt_splice', 'idler_replacement', 'pulley_lagging', 'belt_tracking'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Main haulage conveyor', downtimeCostMultiplier: 20 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Process feed conveyor', downtimeCostMultiplier: 12 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Transfer conveyor', downtimeCostMultiplier: 6 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Stacking conveyor', downtimeCostMultiplier: 3 },
      ],
    },
    {
      id: 'drill_rig',
      name: 'Drill Rig',
      icon: 'Target',
      description: 'Rotary and percussion drill rigs for blast hole drilling',
      category: 'Drilling',
      defaultMetrics: ['penetration_rate', 'bit_wear', 'compressor_pressure', 'rotation_torque', 'pulldown_force'],
      maintenanceTypes: ['bit_replacement', 'compressor_service', 'mast_inspection', 'pipe_thread_inspection'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary production drill', downtimeCostMultiplier: 18 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Pattern drilling rig', downtimeCostMultiplier: 10 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Secondary drill', downtimeCostMultiplier: 5 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Exploration drill', downtimeCostMultiplier: 3 },
      ],
    },
  ],

  kpiDomains: [
    {
      id: 'availability_utilization',
      name: 'Availability & Utilization',
      icon: 'Clock',
      description: 'Equipment availability and operational utilization metrics',
      businessValue: 'Maximize productive operating hours and minimize unplanned downtime',
      category: 'universal',
      metrics: [
        { id: 'physical_availability', name: 'Physical Availability', unit: '%', description: 'Available time vs scheduled time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 90, warning: 80, critical: 70 } },
        { id: 'use_of_availability', name: 'Use of Availability', unit: '%', description: 'Operating time vs available time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 85, warning: 75, critical: 65 } },
        { id: 'effective_utilization', name: 'Effective Utilization', unit: '%', description: 'Productive time vs total time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 75, warning: 60, critical: 50 } },
        { id: 'mtbf', name: 'Mean Time Between Failures', unit: 'hours', description: 'Average operating time between breakdowns', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 500, warning: 300, critical: 150 } },
        { id: 'mttr', name: 'Mean Time To Repair', unit: 'hours', description: 'Average repair duration', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 4, warning: 8, critical: 16 } },
      ],
      thresholds: { healthy: { min: 85 }, warning: { min: 70, max: 85 }, critical: { max: 70 } },
      visualizations: ['gauge', 'line_chart', 'comparison', 'trend'],
    },
    {
      id: 'equipment_health',
      name: 'Equipment Health',
      icon: 'Activity',
      description: 'Real-time condition monitoring and health indicators',
      businessValue: 'Early detection of equipment degradation to prevent catastrophic failures',
      category: 'universal',
      metrics: [
        { id: 'vibration', name: 'Vibration Level', unit: 'mm/s', description: 'Overall vibration velocity', dataType: 'continuous', aggregation: 'max', goodDirection: 'lower', defaultThresholds: { healthy: 4.5, warning: 11.2, critical: 18 } },
        { id: 'oil_condition', name: 'Oil Condition', unit: 'index', description: 'Lubricant degradation index', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 80, warning: 60, critical: 40 } },
        { id: 'temperature', name: 'Operating Temperature', unit: 'C', description: 'Critical component temperature', dataType: 'continuous', aggregation: 'max', goodDirection: 'target', defaultThresholds: { healthy: 85, warning: 100, critical: 115 } },
        { id: 'hydraulic_pressure', name: 'Hydraulic Pressure', unit: 'bar', description: 'System hydraulic pressure', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: 250, warning: 220, critical: 180 } },
        { id: 'health_score', name: 'Overall Health Score', unit: '%', description: 'Composite equipment health', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 85, warning: 70, critical: 50 } },
      ],
      thresholds: { healthy: { min: 85 }, warning: { min: 70, max: 85 }, critical: { max: 70 } },
      visualizations: ['gauge', 'heatmap', 'line_chart', 'distribution'],
    },
    {
      id: 'production_efficiency',
      name: 'Production Efficiency',
      icon: 'TrendingUp',
      description: 'Throughput and productivity metrics for mining operations',
      businessValue: 'Optimize material movement and processing rates',
      category: 'industry_specific',
      metrics: [
        { id: 'tons_per_hour', name: 'Tons Per Hour', unit: 't/hr', description: 'Material throughput rate', dataType: 'continuous', aggregation: 'sum', goodDirection: 'higher', defaultThresholds: { healthy: 1000, warning: 800, critical: 600 } },
        { id: 'cycle_time', name: 'Cycle Time', unit: 'min', description: 'Complete haul cycle duration', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 20, warning: 28, critical: 35 } },
        { id: 'payload_factor', name: 'Payload Factor', unit: '%', description: 'Actual vs rated payload', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: 95, warning: 85, critical: 75 } },
        { id: 'fuel_efficiency', name: 'Fuel Efficiency', unit: 'L/t', description: 'Fuel consumption per ton moved', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 0.8, warning: 1.2, critical: 1.6 } },
        { id: 'crusher_throughput', name: 'Crusher Throughput', unit: 't/hr', description: 'Processing plant throughput', dataType: 'continuous', aggregation: 'sum', goodDirection: 'higher', defaultThresholds: { healthy: 2000, warning: 1600, critical: 1200 } },
      ],
      thresholds: { healthy: { min: 90 }, warning: { min: 75, max: 90 }, critical: { max: 75 } },
      visualizations: ['bar_chart', 'line_chart', 'comparison', 'trend'],
    },
    {
      id: 'safety_compliance',
      name: 'Safety & Compliance',
      icon: 'Shield',
      description: 'Safety performance and regulatory compliance metrics',
      businessValue: 'Ensure worker safety and regulatory compliance to prevent incidents and fines',
      category: 'industry_specific',
      metrics: [
        { id: 'trifr', name: 'Total Recordable Injury Frequency Rate', unit: 'per M hrs', description: 'Recordable injuries per million hours', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 2, warning: 5, critical: 10 } },
        { id: 'near_miss_ratio', name: 'Near Miss Ratio', unit: 'ratio', description: 'Near misses reported per incident', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 10, warning: 5, critical: 2 } },
        { id: 'inspection_compliance', name: 'Inspection Compliance', unit: '%', description: 'Inspections completed on time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 95, critical: 90 } },
        { id: 'training_currency', name: 'Training Currency', unit: '%', description: 'Personnel with current certifications', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 95, critical: 85 } },
        { id: 'permit_compliance', name: 'Permit Compliance', unit: '%', description: 'Operations within permit requirements', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 98, critical: 95 } },
      ],
      thresholds: { healthy: { min: 95 }, warning: { min: 85, max: 95 }, critical: { max: 85 } },
      visualizations: ['gauge', 'bar_chart', 'trend', 'comparison'],
    },
  ],

  maintenanceTypes: [
    {
      id: 'preventive',
      name: 'Preventive Maintenance',
      description: 'Scheduled maintenance based on hours or calendar intervals',
      category: 'preventive',
      defaultInterval: { value: 250, unit: 'hours' },
      laborRequirements: [
        { role: 'Heavy Equipment Technician', count: 2, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 4, typical: 8, max: 16, unit: 'hours' },
      skillsRequired: ['heavy_equipment', 'hydraulics', 'diesel_engines'],
      partsCommonly: ['filters', 'lubricants', 'seals', 'wear_items'],
    },
    {
      id: 'corrective',
      name: 'Corrective Maintenance',
      description: 'Repair work performed after equipment breakdown',
      category: 'corrective',
      laborRequirements: [
        { role: 'Heavy Equipment Technician', count: 2, hourlyRate: 55 },
        { role: 'Field Service Engineer', count: 1, hourlyRate: 95 },
      ],
      estimatedDuration: { min: 4, typical: 16, max: 72, unit: 'hours' },
      skillsRequired: ['diagnostics', 'heavy_equipment', 'hydraulics', 'electrical'],
      partsCommonly: ['components', 'hydraulic_parts', 'engine_parts', 'structural'],
    },
    {
      id: 'predictive',
      name: 'Predictive Maintenance',
      description: 'Condition-based maintenance triggered by monitoring data',
      category: 'predictive',
      laborRequirements: [
        { role: 'Reliability Technician', count: 1, hourlyRate: 65 },
        { role: 'Heavy Equipment Technician', count: 2, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 2, typical: 8, max: 24, unit: 'hours' },
      skillsRequired: ['condition_monitoring', 'vibration_analysis', 'oil_analysis', 'heavy_equipment'],
      partsCommonly: ['bearings', 'seals', 'wear_components', 'sensors'],
    },
    {
      id: 'condition_based',
      name: 'Condition-Based Maintenance',
      description: 'Maintenance triggered by specific condition thresholds',
      category: 'condition_based',
      laborRequirements: [
        { role: 'Heavy Equipment Technician', count: 2, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 2, typical: 6, max: 12, unit: 'hours' },
      skillsRequired: ['heavy_equipment', 'diagnostics'],
      partsCommonly: ['wear_parts', 'filters', 'seals'],
    },
  ],

  riskFactors: [
    { id: 'operational', name: 'Operational Risk', description: 'Risk of unplanned downtime affecting production', category: 'operational', weight: 0.30, calculationMethod: 'exponential', inputs: ['health_score', 'age', 'maintenance_backlog', 'utilization'] },
    { id: 'financial', name: 'Financial Risk', description: 'Risk of excessive repair costs or lost production value', category: 'financial', weight: 0.25, calculationMethod: 'linear', inputs: ['repair_history', 'parts_availability', 'component_age'] },
    { id: 'safety', name: 'Safety Risk', description: 'Risk of safety incidents or injuries', category: 'safety', weight: 0.30, calculationMethod: 'threshold', inputs: ['inspection_status', 'safety_device_status', 'operator_certification'] },
    { id: 'environmental', name: 'Environmental Risk', description: 'Risk of environmental incidents or permit violations', category: 'environmental', weight: 0.15, calculationMethod: 'threshold', inputs: ['emission_levels', 'spill_containment', 'dust_suppression'] },
  ],

  complianceStandards: [
    {
      id: 'msha',
      name: 'Mine Safety and Health Administration',
      shortName: 'MSHA',
      description: 'Federal agency regulating mining safety and health in the United States',
      authority: 'US Department of Labor',
      region: ['US'],
      requirements: [
        { id: 'pre_shift_inspection', name: 'Pre-Shift Inspection', description: 'Daily equipment inspection before operation', kpiId: 'inspection_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'training_certification', name: 'Training Certification', description: 'All personnel properly trained and certified', kpiId: 'training_currency', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'equipment_guarding', name: 'Equipment Guarding', description: 'All moving parts properly guarded', kpiId: 'guard_status', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'dust_monitoring', name: 'Respirable Dust Monitoring', description: 'Dust levels within permissible limits', kpiId: 'dust_level', threshold: 1.5, operator: 'lte', severity: 'required' },
      ],
      reportingFrequency: 'daily',
    },
    {
      id: 'iso_45001',
      name: 'ISO 45001 Occupational Health and Safety',
      shortName: 'ISO 45001',
      description: 'International standard for occupational health and safety management systems',
      authority: 'ISO',
      region: ['Global'],
      requirements: [
        { id: 'hazard_identification', name: 'Hazard Identification', description: 'Systematic hazard identification and risk assessment', kpiId: 'risk_assessment_complete', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'incident_investigation', name: 'Incident Investigation', description: 'All incidents investigated within timeframe', kpiId: 'investigation_compliance', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'management_review', name: 'Management Review', description: 'Regular safety management reviews', kpiId: 'review_compliance', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'continual_improvement', name: 'Continual Improvement', description: 'Documented improvement in safety metrics', kpiId: 'trifr', threshold: 0, operator: 'lt', severity: 'advisory' },
      ],
      reportingFrequency: 'monthly',
    },
  ],

  professionalRoles: [
    // SME Roles
    {
      id: 'mine_engineer',
      category: 'sme',
      name: 'Mine Engineer',
      shortName: 'ME',
      description: 'Plans and optimizes mining operations including equipment fleet and production schedules',
      responsibilities: [
        'Mine planning and scheduling',
        'Equipment fleet optimization',
        'Production target setting',
        'Cost analysis and budgeting',
        'Technical support for operations',
      ],
      focusAreas: ['production_rates', 'equipment_utilization', 'cost_per_ton', 'fleet_efficiency', 'mine_planning'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'business_impact', 'action_items'],
      },
      decisionAuthority: ['production_schedules', 'equipment_allocation', 'operational_parameters'],
      reportingTo: 'Mining Manager',
      icon: 'HardHat',
    },
    // Technician Roles
    {
      id: 'maintenance_technician',
      category: 'technician',
      name: 'Heavy Equipment Technician',
      shortName: 'HET',
      description: 'Performs maintenance and repairs on heavy mining equipment',
      responsibilities: [
        'Equipment troubleshooting and repair',
        'Preventive maintenance execution',
        'Component rebuilds and replacements',
        'Field service and breakdown response',
        'Work order documentation',
      ],
      focusAreas: ['fault_codes', 'repair_procedures', 'parts_needed', 'safety_requirements', 'tool_requirements'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: false,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'action_items'],
      },
      decisionAuthority: ['repair_methods', 'parts_selection', 'immediate_safety_stops'],
      reportingTo: 'Maintenance Supervisor',
      icon: 'Wrench',
    },
    // Executive Roles
    {
      id: 'operations_manager',
      category: 'executive',
      name: 'Operations Manager',
      shortName: 'OM',
      description: 'Oversees daily mining operations and production targets',
      responsibilities: [
        'Production management',
        'Safety oversight',
        'Budget management',
        'Team leadership',
        'Stakeholder communication',
      ],
      focusAreas: ['production_targets', 'safety_performance', 'cost_management', 'equipment_availability', 'workforce_management'],
      explanationStyle: {
        depth: 'high_level',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive',
        focusOn: ['business_impact', 'risk', 'compliance'],
      },
      decisionAuthority: ['operational_priorities', 'resource_allocation', 'budget_decisions'],
      reportingTo: 'Site General Manager',
      icon: 'Users',
    },
    // Analyst Roles
    {
      id: 'equipment_analyst',
      category: 'analyst',
      name: 'Equipment Analyst',
      shortName: 'EA',
      description: 'Analyzes equipment performance data to optimize fleet efficiency',
      responsibilities: [
        'Fleet performance analysis',
        'Condition monitoring data review',
        'Maintenance optimization',
        'KPI tracking and reporting',
        'Trend analysis and forecasting',
      ],
      focusAreas: ['fleet_metrics', 'condition_data', 'maintenance_costs', 'reliability_trends', 'benchmarking'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['technical_details', 'business_impact'],
      },
      decisionAuthority: ['analysis_priorities', 'report_formats', 'data_collection_methods'],
      reportingTo: 'Maintenance Manager',
      icon: 'BarChart',
    },
  ],

  partsCatalog: {
    categories: [
      {
        id: 'undercarriage',
        name: 'Undercarriage Components',
        description: 'Track system and undercarriage wear components for tracked equipment',
        parts: [
          {
            id: 'track_chain',
            partNumber: 'CAT-9W9354',
            name: 'Track Chain Assembly',
            description: 'Complete track chain assembly for large mining excavators',
            category: 'undercarriage',
            manufacturer: 'Caterpillar',
            unitCost: 45000.00,
            currency: 'USD',
            leadTime: { value: 21, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['excavator'],
            failureModes: ['track_wear', 'link_failure', 'bushing_wear'],
            averageLifespan: { value: 6000, unit: 'hours' },
            specifications: { pitch: '260mm', links: 49, bushingOD: '127mm' },
          },
          {
            id: 'track_shoe',
            partNumber: 'CAT-1959240',
            name: 'Track Shoe - Triple Grouser',
            description: '900mm triple grouser track shoe for soft ground applications',
            category: 'undercarriage',
            manufacturer: 'Caterpillar',
            unitCost: 850.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['excavator', 'drill_rig'],
            failureModes: ['shoe_wear', 'grouser_damage'],
            averageLifespan: { value: 4000, unit: 'hours' },
            specifications: { width: '900mm', grouserHeight: '75mm' },
          },
          {
            id: 'idler_front',
            partNumber: 'CAT-2661066',
            name: 'Front Idler Assembly',
            description: 'Front idler with bearings for track tensioning',
            category: 'undercarriage',
            manufacturer: 'Caterpillar',
            unitCost: 12500.00,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['excavator', 'drill_rig'],
            failureModes: ['bearing_failure', 'seal_leak', 'idler_wear'],
            averageLifespan: { value: 8000, unit: 'hours' },
          },
          {
            id: 'sprocket_segment',
            partNumber: 'CAT-3092590',
            name: 'Drive Sprocket Segment',
            description: 'Replaceable sprocket segment set for final drive',
            category: 'undercarriage',
            manufacturer: 'Caterpillar',
            unitCost: 3200.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['excavator'],
            failureModes: ['sprocket_wear', 'tooth_damage'],
            averageLifespan: { value: 5000, unit: 'hours' },
            specifications: { segments: 5, teethPerSegment: 5 },
          },
        ],
      },
      {
        id: 'hydraulics',
        name: 'Hydraulic Components',
        description: 'Hydraulic pumps, cylinders, valves, and hoses for heavy equipment',
        parts: [
          {
            id: 'main_pump',
            partNumber: 'KAW-708-2L-00790',
            name: 'Main Hydraulic Pump Assembly',
            description: 'Variable displacement axial piston pump for excavator main circuit',
            category: 'hydraulics',
            manufacturer: 'Kawasaki',
            unitCost: 28000.00,
            currency: 'USD',
            leadTime: { value: 28, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['excavator', 'loader'],
            failureModes: ['pump_wear', 'seal_failure', 'swashplate_damage'],
            averageLifespan: { value: 15000, unit: 'hours' },
            specifications: { displacement: '457cc/rev', maxPressure: '350bar' },
          },
          {
            id: 'boom_cylinder',
            partNumber: 'CAT-2478847',
            name: 'Boom Cylinder Assembly',
            description: 'Complete boom lift cylinder with seals and rod',
            category: 'hydraulics',
            manufacturer: 'Caterpillar',
            unitCost: 18500.00,
            currency: 'USD',
            leadTime: { value: 21, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['excavator'],
            failureModes: ['seal_leak', 'rod_scoring', 'barrel_wear'],
            averageLifespan: { value: 20000, unit: 'hours' },
            specifications: { bore: '280mm', stroke: '2100mm', rodDia: '200mm' },
          },
          {
            id: 'cylinder_seal_kit',
            partNumber: 'CAT-2344799',
            name: 'Cylinder Seal Kit - Boom',
            description: 'Complete seal kit for boom cylinder rebuild',
            category: 'hydraulics',
            manufacturer: 'Caterpillar',
            unitCost: 1850.00,
            currency: 'USD',
            leadTime: { value: 5, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['excavator'],
            failureModes: ['seal_leak', 'cylinder_drift'],
            averageLifespan: { value: 6000, unit: 'hours' },
          },
          {
            id: 'control_valve',
            partNumber: 'KAW-KMX15YC',
            name: 'Main Control Valve Bank',
            description: 'Multi-function hydraulic control valve assembly',
            category: 'hydraulics',
            manufacturer: 'Kawasaki',
            unitCost: 22000.00,
            currency: 'USD',
            leadTime: { value: 35, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['excavator'],
            failureModes: ['spool_wear', 'seal_leak', 'relief_failure'],
            averageLifespan: { value: 25000, unit: 'hours' },
          },
        ],
      },
      {
        id: 'electrical',
        name: 'Electrical Components',
        description: 'Electrical systems, sensors, and control components',
        parts: [
          {
            id: 'alternator',
            partNumber: 'DEL-29SI-24V',
            name: 'Alternator 200A 24V',
            description: 'Heavy-duty alternator for mining equipment electrical system',
            category: 'electrical',
            manufacturer: 'Delco Remy',
            unitCost: 1800.00,
            currency: 'USD',
            leadTime: { value: 5, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['haul_truck', 'excavator', 'loader', 'drill_rig'],
            failureModes: ['bearing_failure', 'diode_failure', 'regulator_failure'],
            averageLifespan: { value: 8000, unit: 'hours' },
            specifications: { output: '200A', voltage: '24V' },
          },
          {
            id: 'starter_motor',
            partNumber: 'DEL-42MT-24V',
            name: 'Starter Motor 24V',
            description: 'Heavy-duty starter motor for large diesel engines',
            category: 'electrical',
            manufacturer: 'Delco Remy',
            unitCost: 2400.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['haul_truck', 'excavator', 'loader', 'drill_rig'],
            failureModes: ['solenoid_failure', 'bendix_wear', 'motor_burnout'],
            averageLifespan: { value: 6000, unit: 'hours' },
            specifications: { power: '11kW', voltage: '24V' },
          },
          {
            id: 'pressure_sensor',
            partNumber: 'CAT-3661638',
            name: 'Hydraulic Pressure Sensor',
            description: 'Pressure transducer for hydraulic system monitoring',
            category: 'electrical',
            manufacturer: 'Caterpillar',
            unitCost: 450.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['excavator', 'loader', 'haul_truck'],
            failureModes: ['sensor_drift', 'connector_damage', 'diaphragm_failure'],
            averageLifespan: { value: 10000, unit: 'hours' },
            specifications: { range: '0-600bar', output: '4-20mA' },
          },
          {
            id: 'ecm',
            partNumber: 'CAT-3879431',
            name: 'Engine Control Module',
            description: 'Electronic control module for engine management',
            category: 'electrical',
            manufacturer: 'Caterpillar',
            unitCost: 8500.00,
            currency: 'USD',
            leadTime: { value: 10, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['haul_truck', 'excavator', 'loader', 'drill_rig'],
            failureModes: ['software_fault', 'connector_corrosion', 'component_failure'],
            averageLifespan: { value: 30000, unit: 'hours' },
          },
        ],
      },
      {
        id: 'wear_parts',
        name: 'Ground Engaging & Wear Parts',
        description: 'Bucket teeth, cutting edges, and other wear components',
        parts: [
          {
            id: 'bucket_tooth',
            partNumber: 'ESCO-V61SYL',
            name: 'Excavator Bucket Tooth',
            description: 'Heavy-duty rock tooth for mining excavator bucket',
            category: 'wear_parts',
            manufacturer: 'ESCO',
            unitCost: 185.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['excavator', 'loader'],
            failureModes: ['tooth_wear', 'tooth_breakage', 'adapter_wear'],
            averageLifespan: { value: 400, unit: 'hours' },
            specifications: { style: 'V-Series', application: 'Rock' },
          },
          {
            id: 'tooth_adapter',
            partNumber: 'ESCO-V61WN',
            name: 'Tooth Adapter Weld-On',
            description: 'Weld-on adapter for V-series bucket teeth',
            category: 'wear_parts',
            manufacturer: 'ESCO',
            unitCost: 95.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['excavator', 'loader'],
            failureModes: ['adapter_wear', 'weld_failure'],
            averageLifespan: { value: 2000, unit: 'hours' },
          },
          {
            id: 'cutting_edge',
            partNumber: 'CAT-1U3352',
            name: 'Bucket Cutting Edge',
            description: 'Bolt-on cutting edge for excavator bucket lip',
            category: 'wear_parts',
            manufacturer: 'Caterpillar',
            unitCost: 650.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['excavator', 'loader'],
            failureModes: ['edge_wear', 'bolt_failure'],
            averageLifespan: { value: 800, unit: 'hours' },
            specifications: { length: '2000mm', thickness: '38mm' },
          },
          {
            id: 'crusher_liner',
            partNumber: 'META-CL-GYR',
            name: 'Gyratory Crusher Mantle',
            description: 'Manganese steel mantle for primary gyratory crusher',
            category: 'wear_parts',
            manufacturer: 'Metso',
            unitCost: 85000.00,
            currency: 'USD',
            leadTime: { value: 60, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['crusher'],
            failureModes: ['liner_wear', 'cracking', 'gouging'],
            averageLifespan: { value: 4000, unit: 'hours' },
            specifications: { material: '18% Mn Steel', weight: '12000kg' },
          },
          {
            id: 'conveyor_belt',
            partNumber: 'FENNER-ST3150',
            name: 'Steel Cord Conveyor Belt',
            description: 'Heavy-duty steel cord conveyor belt for overland conveyor',
            category: 'wear_parts',
            manufacturer: 'Fenner Dunlop',
            unitCost: 450.00,
            currency: 'USD',
            leadTime: { value: 45, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['conveyor_system'],
            failureModes: ['belt_wear', 'cord_damage', 'splice_failure'],
            averageLifespan: { value: 25000, unit: 'hours' },
            specifications: { strength: 'ST3150', width: '1800mm', topCover: '12mm' },
          },
        ],
      },
    ],
  },

  rootCauseLibrary: {
    categories: [
      {
        id: 'mechanical',
        name: 'Mechanical',
        description: 'Root causes related to mechanical systems in heavy mining equipment',
        icon: 'Cog',
        color: '#3b82f6',
        rootCauses: [
          {
            id: 'final_drive_failure',
            name: 'Final Drive Failure',
            description: 'Failure of planetary gear system or bearings in track or wheel final drives',
            category: 'mechanical',
            symptoms: [
              'Unusual noise from drive area',
              'Oil leakage from final drive',
              'Overheating of final drive housing',
              'Loss of drive power',
              'Metal particles in oil sample',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Collect oil sample from final drive',
                expectedResult: 'Sample shows metal particle contamination or wear metals',
                tools: ['Sample bottles', 'Vacuum pump'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Check final drive oil level and condition',
                expectedResult: 'Oil level correct, no water contamination or burning smell',
                tools: ['Dipstick', 'Flashlight'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Measure final drive case temperature',
                expectedResult: 'Temperature within normal range (below 95C)',
                tools: ['IR thermometer'],
                duration: 5,
                skillLevel: 'basic',
              },
              {
                order: 4,
                action: 'Perform travel motor pressure test',
                expectedResult: 'Pressure readings match specifications',
                tools: ['Pressure gauges', 'Diagnostic software'],
                duration: 30,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Regular oil sampling every 250 hours',
              'Maintain correct oil levels',
              'Avoid excessive track tension',
              'Limit counter-rotation steering',
              'Monitor temperature during operation',
            ],
            typicalProbability: 0.18,
            affectedComponents: ['planetary_gears', 'bearings', 'seals', 'housing'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 24, typical: 48, max: 120, unit: 'hours' },
            costRange: { min: 15000, max: 85000, currency: 'USD' },
            industryReferences: ['Caterpillar SEBF8191', 'Komatsu Shop Manual'],
          },
          {
            id: 'undercarriage_wear',
            name: 'Accelerated Undercarriage Wear',
            description: 'Premature wear of track components due to improper tension, contamination, or operating conditions',
            category: 'mechanical',
            symptoms: [
              'Excessive track sag',
              'Track coming off during operation',
              'Visible wear on sprocket teeth',
              'Bushing rotation (dry joint)',
              'Link pitch elongation',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Measure track sag',
                expectedResult: 'Sag within manufacturer specification',
                tools: ['Measuring tape', 'Straight edge'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Measure component wear using ultrasonic tools',
                expectedResult: 'Wear within acceptable limits for hours',
                tools: ['Ultrasonic wear gauge'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Inspect for bushing rotation',
                expectedResult: 'No dry joints visible',
                tools: ['Flashlight', 'Scribe'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 4,
                action: 'Check idler and roller wear',
                expectedResult: 'Wear consistent with track wear',
                tools: ['Wear gauge', 'Caliper'],
                duration: 20,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Maintain correct track tension',
              'Regular undercarriage inspections',
              'Match undercarriage to application',
              'Minimize counter-rotation',
              'Keep undercarriage clean',
            ],
            typicalProbability: 0.22,
            affectedComponents: ['track_chain', 'sprocket', 'idlers', 'rollers', 'shoes'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 16, typical: 40, max: 80, unit: 'hours' },
            costRange: { min: 25000, max: 150000, currency: 'USD' },
            industryReferences: ['Caterpillar SEBU6250', 'Komatsu Undercarriage Manual'],
          },
        ],
      },
      {
        id: 'hydraulic',
        name: 'Hydraulic',
        description: 'Root causes related to hydraulic systems in mining equipment',
        icon: 'Droplets',
        color: '#06b6d4',
        rootCauses: [
          {
            id: 'pump_failure',
            name: 'Hydraulic Pump Failure',
            description: 'Degradation or failure of main hydraulic pumps causing loss of hydraulic power',
            category: 'hydraulic',
            symptoms: [
              'Reduced hydraulic power',
              'Slow implement response',
              'Increased cycle times',
              'Unusual pump noise',
              'Elevated oil temperature',
              'Low system pressure',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Check pump discharge pressure',
                expectedResult: 'Pressure meets specification at full load',
                tools: ['Digital pressure gauge', 'Diagnostic software'],
                duration: 15,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Measure pump flow rate',
                expectedResult: 'Flow within 10% of rated capacity',
                tools: ['Flow meter'],
                duration: 20,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Check hydraulic oil condition',
                expectedResult: 'Oil clean, no water or contamination',
                tools: ['Oil sample kit', 'Patch test'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 4,
                action: 'Inspect pump case drain flow',
                expectedResult: 'Case drain flow below maximum specification',
                tools: ['Flow meter', 'Collection container'],
                duration: 15,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Regular hydraulic oil sampling',
              'Maintain oil cleanliness (NAS 8 or better)',
              'Monitor oil temperature',
              'Change filters at recommended intervals',
              'Warm up hydraulics before full load operation',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['pump_pistons', 'swashplate', 'bearings', 'seals', 'valve_plate'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 8, typical: 24, max: 48, unit: 'hours' },
            costRange: { min: 20000, max: 65000, currency: 'USD' },
            industryReferences: ['SAE J1453', 'ISO 4406'],
          },
          {
            id: 'cylinder_leak',
            name: 'Hydraulic Cylinder Internal Leak',
            description: 'Internal seal failure causing cylinder drift and reduced efficiency',
            category: 'hydraulic',
            symptoms: [
              'Implement drift when holding load',
              'Reduced lifting capacity',
              'Slow implement movement',
              'Cylinder bypass audible',
              'Elevated oil temperature',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform cylinder drift test',
                expectedResult: 'Drift less than specified amount over time',
                tools: ['Stopwatch', 'Measuring tape'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Check cylinder port pressures',
                expectedResult: 'Equal pressure both sides with implement loaded',
                tools: ['Pressure gauges'],
                duration: 20,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Inspect cylinder rod for damage',
                expectedResult: 'No scoring, pitting, or damage visible',
                tools: ['Flashlight', 'Magnifying glass'],
                duration: 10,
                skillLevel: 'basic',
              },
            ],
            preventiveActions: [
              'Maintain hydraulic oil cleanliness',
              'Protect cylinder rods from damage',
              'Replace seals at recommended intervals',
              'Avoid overloading implements',
              'Monitor cylinder performance trends',
            ],
            typicalProbability: 0.20,
            affectedComponents: ['piston_seals', 'rod_seals', 'wear_rings', 'cylinder_rod'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 8, typical: 16, max: 32, unit: 'hours' },
            costRange: { min: 2000, max: 25000, currency: 'USD' },
          },
        ],
      },
      {
        id: 'electrical',
        name: 'Electrical',
        description: 'Root causes related to electrical and electronic systems',
        icon: 'Zap',
        color: '#f59e0b',
        rootCauses: [
          {
            id: 'sensor_failure',
            name: 'Sensor Failure',
            description: 'Failure or drift of critical sensors affecting equipment operation or monitoring',
            category: 'electrical',
            symptoms: [
              'Erratic gauge readings',
              'Warning lights with no apparent cause',
              'Engine derates',
              'Intermittent fault codes',
              'Equipment shutdown',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Read active and logged fault codes',
                expectedResult: 'Identify sensor-related codes',
                tools: ['Diagnostic laptop', 'Service software'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Check sensor wiring and connectors',
                expectedResult: 'No damaged wires, corroded or loose connectors',
                tools: ['Multimeter', 'Flashlight'],
                duration: 20,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Compare sensor reading to manual measurement',
                expectedResult: 'Sensor reading matches actual value',
                tools: ['Reference instrument (thermocouple, pressure gauge)'],
                duration: 15,
                skillLevel: 'intermediate',
              },
              {
                order: 4,
                action: 'Perform sensor calibration or replacement',
                expectedResult: 'Sensor reads correctly after calibration',
                tools: ['Calibration equipment', 'New sensor'],
                duration: 30,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Regular sensor inspection and cleaning',
              'Protect connectors from moisture and dust',
              'Replace sensors at recommended intervals',
              'Monitor sensor trends for drift',
              'Use quality replacement parts',
            ],
            typicalProbability: 0.18,
            affectedComponents: ['pressure_sensors', 'temperature_sensors', 'position_sensors', 'speed_sensors'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 1, typical: 4, max: 8, unit: 'hours' },
            costRange: { min: 200, max: 2500, currency: 'USD' },
          },
          {
            id: 'harness_damage',
            name: 'Wiring Harness Damage',
            description: 'Physical damage to wiring harnesses causing intermittent or complete electrical failures',
            category: 'electrical',
            symptoms: [
              'Intermittent electrical faults',
              'Multiple unrelated fault codes',
              'Functions failing under vibration',
              'Burnt smell from wiring',
              'Visible wire damage',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Visually inspect harness routing',
                expectedResult: 'No chafing, pinching, or heat damage',
                tools: ['Flashlight', 'Mirror'],
                duration: 30,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Perform continuity tests on suspect circuits',
                expectedResult: 'Continuity through all conductors',
                tools: ['Multimeter'],
                duration: 20,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Check for shorts to ground',
                expectedResult: 'No unintended ground paths',
                tools: ['Multimeter', 'Insulation tester'],
                duration: 20,
                skillLevel: 'intermediate',
              },
              {
                order: 4,
                action: 'Wiggle test while monitoring',
                expectedResult: 'No intermittent connections during movement',
                tools: ['Diagnostic software', 'Multimeter'],
                duration: 15,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Secure harnesses properly during maintenance',
              'Use correct cable ties and routing',
              'Inspect harnesses during PM',
              'Protect from heat sources',
              'Apply dielectric grease to connectors',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['wiring_harness', 'connectors', 'terminals', 'grommets'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 2, typical: 8, max: 24, unit: 'hours' },
            costRange: { min: 500, max: 8000, currency: 'USD' },
          },
        ],
      },
      {
        id: 'operator_related',
        name: 'Operator Related',
        description: 'Root causes related to equipment operation and human factors',
        icon: 'User',
        color: '#8b5cf6',
        rootCauses: [
          {
            id: 'overloading',
            name: 'Equipment Overloading',
            description: 'Operating equipment beyond rated capacity causing accelerated wear and failures',
            category: 'operator_related',
            symptoms: [
              'Frequent overload alarms',
              'Accelerated wear patterns',
              'Structural cracking',
              'Hydraulic relief activation',
              'Reduced component life',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review payload monitoring data',
                expectedResult: 'Identify overloading events and patterns',
                tools: ['Fleet management system', 'Payload records'],
                duration: 30,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Analyze operator loading practices',
                expectedResult: 'Identify operators with high overload frequency',
                tools: ['Fleet management system'],
                duration: 20,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Inspect structural components for stress',
                expectedResult: 'No cracking or deformation visible',
                tools: ['Dye penetrant', 'Flashlight'],
                duration: 45,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Implement payload management system',
              'Set appropriate loading targets',
              'Provide operator training',
              'Monitor and coach loading practices',
              'Calibrate scales regularly',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['frame', 'suspension', 'tires', 'hydraulics', 'drivetrain'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 2, typical: 8, max: 40, unit: 'hours' },
            costRange: { min: 1000, max: 100000, currency: 'USD' },
          },
          {
            id: 'poor_operation',
            name: 'Improper Operating Technique',
            description: 'Operating practices that cause unnecessary equipment stress and damage',
            category: 'operator_related',
            symptoms: [
              'High fuel consumption',
              'Accelerated tire wear',
              'Transmission issues',
              'Brake system problems',
              'Structural fatigue',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review operator performance metrics',
                expectedResult: 'Identify operators with abnormal metrics',
                tools: ['Fleet management system'],
                duration: 20,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Analyze speed and braking patterns',
                expectedResult: 'Identify harsh braking and speeding events',
                tools: ['Telematics data'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Observe operator during operation',
                expectedResult: 'Document operating practice deviations',
                tools: ['Observation checklist'],
                duration: 60,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Implement operator training programs',
              'Use simulator training',
              'Monitor operator metrics continuously',
              'Provide feedback and coaching',
              'Recognize good operating practices',
            ],
            typicalProbability: 0.18,
            affectedComponents: ['tires', 'brakes', 'transmission', 'engine', 'frame'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 1, typical: 4, max: 16, unit: 'hours' },
            costRange: { min: 500, max: 25000, currency: 'USD' },
          },
        ],
      },
    ],
    commonPatterns: [
      {
        id: 'haul_truck_overload_cascade',
        name: 'Haul Truck Overload Failure Cascade',
        description: 'Progressive damage from consistent overloading leading to major component failure',
        sequence: [
          'Consistent overloading beyond rated capacity',
          'Accelerated tire wear and failures',
          'Suspension stress and fatigue',
          'Frame cracking initiation',
          'Drivetrain stress and premature wear',
          'Major component failure',
        ],
        indicators: [
          'Payload averaging above 110% of rated',
          'Tire life below expected hours',
          'Suspension complaints from operators',
        ],
        preventionStrategy: 'Implement strict payload management with real-time monitoring and operator coaching programs',
      },
      {
        id: 'excavator_hydraulic_cascade',
        name: 'Excavator Hydraulic System Cascade',
        description: 'Progressive hydraulic system degradation from contamination',
        sequence: [
          'Oil contamination ingress',
          'Accelerated pump wear',
          'Control valve wear and leakage',
          'Cylinder seal damage',
          'System overheating',
          'Major pump or motor failure',
        ],
        indicators: [
          'Oil sample showing elevated particle counts',
          'Increasing cycle times',
          'Rising oil temperature trend',
        ],
        preventionStrategy: 'Maintain oil cleanliness through regular sampling, filter changes, and breather maintenance',
      },
      {
        id: 'crusher_liner_cascade',
        name: 'Crusher Liner Wear Cascade',
        description: 'Progressive crusher performance degradation from liner wear',
        sequence: [
          'Normal liner wear progression',
          'Uneven wear pattern development',
          'CSS drift and product variation',
          'Increased power consumption',
          'Throughput reduction',
          'Catastrophic liner failure or shell damage',
        ],
        indicators: [
          'Product size distribution trending coarser',
          'Power draw increasing at same throughput',
          'Visual inspection showing uneven wear',
        ],
        preventionStrategy: 'Implement regular liner measurement program and optimize feed distribution',
      },
    ],
  },

  laborRates: {
    roles: [
      { roleId: 'heavy_equipment_technician', name: 'Heavy Equipment Technician', hourlyRate: 55, currency: 'USD', certifications: ['CAT Certified', 'Komatsu Certified'] },
      { roleId: 'reliability_technician', name: 'Reliability Technician', hourlyRate: 65, currency: 'USD', certifications: ['CMRT', 'Oil Analysis', 'Vibration Analysis'] },
      { roleId: 'electrician', name: 'Industrial Electrician', hourlyRate: 60, currency: 'USD', certifications: ['Journeyman License', 'Mine Electrical'] },
      { roleId: 'field_service_engineer', name: 'Field Service Engineer', hourlyRate: 95, currency: 'USD', certifications: ['OEM Training', 'Hydraulics'] },
      { roleId: 'mine_engineer', name: 'Mine Engineer', hourlyRate: 90, currency: 'USD', certifications: ['PE License', 'Mine Planning'] },
      { roleId: 'maintenance_planner', name: 'Maintenance Planner', hourlyRate: 55, currency: 'USD', certifications: ['CMMS Certification'] },
      { roleId: 'contractor_mechanic', name: 'Contract Heavy Mechanic', hourlyRate: 75, currency: 'USD' },
      { roleId: 'contractor_electrician', name: 'Contract Electrician', hourlyRate: 80, currency: 'USD' },
    ],
    overtimeMultiplier: 1.5,
    emergencyMultiplier: 2.0,
    weekendMultiplier: 1.5,
  },

  visualization: {
    primaryColor: '#3b82f6',
    secondaryColor: '#f59e0b',
    healthyColor: '#10b981',
    warningColor: '#f59e0b',
    criticalColor: '#ef4444',
    chartDefaults: {
      showGrid: true,
      showLegend: true,
      animationDuration: 300,
    },
  },

  narrativeTemplates: [
    {
      id: 'health_summary',
      name: 'Health Summary',
      context: 'asset_health',
      audience: ['executive', 'manager', 'engineer'],
      template: 'Your {assetType} "{assetName}" is currently {healthStatus}. {trendDescription} Over the past {timeRange}, {kpiSummary}. {recommendationText}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'timeRange', type: 'time' },
        { name: 'kpiSummary', type: 'metric' },
        { name: 'recommendationText', type: 'metric' },
      ],
    },
    {
      id: 'prediction_alert',
      name: 'Prediction Alert',
      context: 'prediction',
      audience: ['manager', 'engineer', 'technician'],
      template: 'AI {prediction} for {assetType} "{assetName}": {predictionDetails}. Confidence: {confidence}%. Based on analysis of {dataPoints} data points. Recommended action: {recommendation}',
      variables: [
        { name: 'prediction', type: 'terminology' },
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'predictionDetails', type: 'metric' },
        { name: 'confidence', type: 'percentage' },
        { name: 'dataPoints', type: 'metric' },
        { name: 'recommendation', type: 'metric' },
      ],
    },
    {
      id: 'breakdown_report',
      name: 'Breakdown Report',
      context: 'maintenance',
      audience: ['manager', 'engineer', 'technician'],
      template: '{assetType} "{assetName}" experienced a {failure} at {location}. Initial assessment indicates {rootCause}. Estimated repair time: {repairTime}. Production impact: {productionImpact} tons.',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'failure', type: 'terminology' },
        { name: 'location', type: 'terminology' },
        { name: 'rootCause', type: 'metric' },
        { name: 'repairTime', type: 'time' },
        { name: 'productionImpact', type: 'metric' },
      ],
    },
  ],

  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  defaults: {
    timeRange: '7d',
    refreshInterval: 30,
    defaultDashboard: 'command-center',
  },
}
